'use client'

import React, { useEffect, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import { Box, Link, List, ListItem, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/pro-duotone-svg-icons";
import type Outline from "@/src/_types/outline";

const Outlines = (
    {
        content,
        hasAbstract,
        hasBody
    }: {
        content: string;
        hasAbstract: boolean;
        hasBody: boolean;
    }
) => {
    const [outlines, setOutlines] = useState<Outline[]>([]);
    const listStyle = {
        '& .MuiListItem-root': {
            pt: 0,
            pb: '.25rem',
            pl: '1rem',
            pr: 0,
            fontSize: '.8rem',
            fontWeight: 'bold',
            color: '#788b8b',
            textDecoration: 'none',
            flexWrap: 'wrap',
            flexDirection: 'row',
            wordWrap: 'break-word',
            display: 'flex',
            alignItems: 'center',
            'i': {
                fontSize: '.8rem',
                marginRight: '4px'
            },
            '& a': {
                display: 'flex',
                transition: 'all ease-in-out .2s',
                flexDirection: 'row',
                textDecoration: 'none',
                color: '#788b8b',
                '& > svg': {
                    marginTop: '8px !important'
                },
                '& > span': {
                    display: 'inline'
                },
            },
            '& span': {
                color: 'custom.textColorHierarchy',
                fontSize: '.8rem',
                '&:hover': {
                    cursor: 'pointer',
                    transform: 'scale(1.01)',
                    color: 'primary.main',
                },
                'svg': {
                    marginTop: '0 !important'
                },
            }
        }
    }
    const outlinesDistinguishGuide: string[] = [
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
    ];

    const handleScrollToSection = (
        event: React.MouseEvent<HTMLAnchorElement>,
        sectionId: string
    ) => {
        event.preventDefault();
        const section = document.querySelector(`#${sectionId}`);
        if (section) {
            section.scrollIntoView(
                {
                    behavior: 'smooth'
                }
            );
        }
    };

    useEffect(() => {
        const unescapeHTML = (escapedStr: string) => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = escapedStr;

            return tempDiv.innerHTML.replace(/&nbsp;/g, ' ');
        };

        const contentArray = ReactHtmlParser(content);

        contentArray.forEach((item) => {
            if (item === null) return;

            if (
                typeof item === 'object' &&
                outlinesDistinguishGuide.includes(
                    item.type.toString()
                )
            ) {
                const unescapedLabel = ReactHtmlParser(
                    unescapeHTML(
                        item.props.label
                    )
                );

                setOutlines((prevState) => [
                    ...prevState,
                    {
                        id: item.props.id,
                        label: unescapedLabel
                    } as Outline
                ]);
            }
        });
    }, [content]);

    return (
        (outlines.length > 0) && (
            <Box
                bgcolor="custom.middleGray"
                borderRadius={2}
                p={1}
            >
                <Typography
                    variant="h3"
                    color="custom.hierarchyTitleColor"
                    fontWeight="bold"
                    fontSize={'.975rem'}
                    marginBottom={.4}
                >
                    Outlines
                </Typography>
                <List sx={listStyle}>
                    {
                        hasAbstract &&
                        <ListItem>
                            <Link
                                onClick={(event) => {
                                    handleScrollToSection(
                                        event,
                                        'page-summary'
                                    );
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faCircle}
                                    fontSize={5}
                                    style={{
                                        marginTop: 6,
                                        marginRight: 5
                                    }}
                                />
                                <Typography
                                    component="span"
                                    fontSize={11}
                                >
                                    Abstract
                                </Typography>
                            </Link>
                        </ListItem>
                    }
                    {
                        hasBody &&
                        <ListItem>
                            <Link
                                onClick={(event) => {
                                    handleScrollToSection(
                                        event,
                                        'page-content-body'
                                    );
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faCircle}
                                    fontSize={5}
                                    style={{
                                        marginTop: 6,
                                        marginRight: 5
                                    }}
                                />
                                <Typography
                                    component="span"
                                    fontSize={11}
                                >
                                    Body
                                </Typography>
                            </Link>
                        </ListItem>
                    }
                    {
                        outlines.map((item, index: number) =>
                            <ListItem
                                key={`outline-${index}`}
                            >
                                <Link
                                    onClick={
                                        (event) => handleScrollToSection(event, item.id)
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faCircle}
                                        fontSize={5}
                                        style={{
                                            marginTop: 6,
                                            marginRight: 5
                                        }}
                                    />
                                    <Typography
                                        component="span"
                                    >
                                        {
                                            item.label
                                        }
                                    </Typography>
                                </Link>
                            </ListItem>
                        )
                    }
                </List>
            </Box>
        )
    )
}

export default Outlines;